import {
  ActionButton,
  ArrowPopover,
  Caption,
  Icon,
  MetaValue,
  SubsectionTitle,
  TooltipIconButton,
  useMenu,
} from '@if/mui'
import { Box, Chip, Divider, Stack } from '@mui/material'
import {
  Bell,
  BellOff,
  CalendarCheck,
  CalendarCog,
  CalendarPlus,
  ChevronUp,
  LucideIcon,
  MessageSquarePlus,
  Pencil,
  Plus,
  Repeat,
  RotateCcw,
  Split,
  TrendingDown,
  TrendingUp,
  Trash2,
  Undo2,
  X,
} from 'lucide-react'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { EditPaymentOperation, EEditPaymentOperationKind } from './EditPayments-operations'

const OPERATION_ICON: Record<EEditPaymentOperationKind, LucideIcon> = {
  [EEditPaymentOperationKind.ManualEdits]: Pencil,
  [EEditPaymentOperationKind.AddPayment]: Plus,
  [EEditPaymentOperationKind.AddRecurringPayments]: Repeat,
  [EEditPaymentOperationKind.CatchUpAmountOfOverdueForDate]: CalendarPlus,
  [EEditPaymentOperationKind.CatchUpOnRemainingPayments]: CalendarCheck,
  [EEditPaymentOperationKind.SpreadLateAmount]: Split,
  [EEditPaymentOperationKind.IncreasePayments]: TrendingUp,
  [EEditPaymentOperationKind.ReducePayments]: TrendingDown,
  [EEditPaymentOperationKind.MovePayments]: CalendarCog,
  [EEditPaymentOperationKind.AddNoteToAllPayments]: MessageSquarePlus,
  [EEditPaymentOperationKind.EnableSmsReminders]: Bell,
  [EEditPaymentOperationKind.DisableSmsReminders]: BellOff,
  [EEditPaymentOperationKind.DeletePayment]: Trash2,
  [EEditPaymentOperationKind.RestorePayment]: RotateCcw,
  [EEditPaymentOperationKind.DeleteAllPayments]: Trash2,
}

interface EditPaymentsReviewChangesProps {
  operations: EditPaymentOperation[]
  onUndoTo: (operationId: string) => void
  loading?: boolean
  disabled?: boolean
}

const EditPaymentsReviewChanges = ({
  operations,
  onUndoTo,
  loading = false,
  disabled = false,
}: EditPaymentsReviewChangesProps) => {
  const { t } = useTranslation()
  const [buttonProps, popoverProps, closePopover] = useMenu()

  if (operations.length === 0) return null

  const lastIndex = operations.length - 1

  const describeCounts = (operation: EditPaymentOperation) =>
    [
      operation.counts.changed > 0 && t('loan.payment.editPayments.log.changed', { count: operation.counts.changed }),
      operation.counts.added > 0 && t('loan.payment.editPayments.log.added', { count: operation.counts.added }),
      operation.counts.removed > 0 && t('loan.payment.editPayments.log.removed', { count: operation.counts.removed }),
    ]
      .filter(Boolean)
      .join(' · ')

  return (
    <>
      <ActionButton {...buttonProps} startIcon={RotateCcw} endIcon={ChevronUp} loading={loading} disabled={disabled}>
        <Stack direction="row" spacing={1} alignItems="center" component="span">
          <span>{t('loan.payment.editPayments.log.review')}</span>
          <Chip size="small" color="primary" label={operations.length} />
        </Stack>
      </ActionButton>

      <ArrowPopover {...popoverProps} side="top">
        <Stack sx={{ minWidth: 340, maxWidth: 440 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, pt: 2, pb: 1 }}
          >
            <SubsectionTitle>{t('loan.payment.editPayments.log.title')}</SubsectionTitle>
            <TooltipIconButton title={t('common.close')} size="small" onClick={closePopover}>
              <Icon icon={X} size="sm" />
            </TooltipIconButton>
          </Stack>

          {operations.map((operation, index) => {
            const isLast = index === lastIndex
            return (
              <Box key={operation.id}>
                <Divider />
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.5 }}>
                  <Icon icon={OPERATION_ICON[operation.kind]} size="sm" color="action" />
                  <MetaValue sx={{ flexGrow: 1 }}>
                    {t(`loan.payment.editPayments.operations.${operation.kind}`, operation.labelValues ?? {})}
                  </MetaValue>
                  <Caption>{describeCounts(operation)}</Caption>
                  <ActionButton
                    size="small"
                    startIcon={Undo2}
                    onClick={() => {
                      onUndoTo(operation.id)
                      closePopover()
                    }}
                    disabled={disabled || loading}
                  >
                    {isLast ? t('loan.payment.editPayments.log.undo') : t('loan.payment.editPayments.log.undoToHere')}
                  </ActionButton>
                </Stack>
              </Box>
            )
          })}

          <Divider />
          <Caption sx={{ px: 2, py: 1.5 }}>{t('loan.payment.editPayments.log.hint')}</Caption>
        </Stack>
      </ArrowPopover>
    </>
  )
}

export default memo(EditPaymentsReviewChanges)
